/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
/* eslint-disable no-multiple-empty-lines */
/* eslint-disable import-helpers/order-imports */

import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { format, parseISO, startOfDay, startOfMonth, endOfDay, endOfMonth } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import AlertModal from '../../components/AlertModal';
import { AppContext } from '../../contexts/app';
import CustomTable from '../../components/CustomTable';
import DatePickerMaskedInput from '../../components/DatePickerMaskedInput';
import FinanceiroEdit from '../../components/Financeiro/Edit';
import NicashReembolsoCreate from '../../components/NicashReembolso/Create';
import NicashReembolsoEdit from '../../components/NicashReembolso/Edit';
import api from '../../services/api';
import useNicashReembolsos from './useNicashReembolsos';
import { numberToCurrency } from '../../utils/functions';

const LIMIT = 10;
const COLUMNS_STORAGE_KEY_NICASH_REEMBOLSOS = 'custom-table-nicash-reembolsos-columns';
const DEFAULT_HIDDEN_COLUMNS_NICASH_REEMBOLSOS = [
  'salaAtendimento',
  'diaSemana',
  'numeroCartao',
  'tipoDocumento',
  'numeroDocumento',
  'filial',
  'grupo',
  'responsavelPeloGasto',
  'cnpj',
  'razaoSocial',
];

function NicashReembolsos() {
  const navigate = useNavigate();
  const { loading, notificationRef, setLoading } = useContext(AppContext);
  const alertRef = useRef();
  const modalFinanceiroEdit = useRef();
  const modalNicashReembolsoEdit = useRef();
  const modalNicashReembolsoCreate = useRef();
  const customTableRef = useRef();

  const [page, setPage] = useState(1);
  const [dataInicial, setDataInicial] = useState(() => startOfDay(startOfMonth(new Date())));
  const [dataFinal, setDataFinal] = useState(() => endOfDay(endOfMonth(new Date())));
  const [listLoading, setListLoading] = useState(true);
  const [tableFilters, setTableFilters] = useState([]);
  const [selectedRowAction, setSelectedRowAction] = useState(null);
  const [showEditDecisionModal, setShowEditDecisionModal] = useState(false);
  const [showFinanceiroChoiceModal, setShowFinanceiroChoiceModal] = useState(false);

  const formattedDataInicial = useMemo(
    () => (dataInicial ? format(dataInicial, 'yyyy-MM-dd') : ''),
    [dataInicial],
  );

  const formattedDataFinal = useMemo(
    () => (dataFinal ? format(dataFinal, 'yyyy-MM-dd') : ''),
    [dataFinal],
  );

  const filtrosQuery = useMemo(() => {
    const profissionalNome = tableFilters.find((s) => s.field === 'profissionalNome')?.value || '';
    const clienteNome = tableFilters.find((s) => s.field === 'clienteNome')?.value || '';
    const startValorAReceber = tableFilters.find((s) => s.field === 'startvalorAReceber')?.startRange;
    const endValorAReceber = tableFilters.find((s) => s.field === 'endvalorAReceber')?.endRange;
    const startValorAPagar = tableFilters.find((s) => s.field === 'startvalorAPagar')?.startRange;
    const endValorAPagar = tableFilters.find((s) => s.field === 'endvalorAPagar')?.endRange;

    return {
      profissionalNome: profissionalNome.trim(),
      clienteNome: clienteNome.trim(),
      startValorAReceber,
      endValorAReceber,
      startValorAPagar,
      endValorAPagar,
    };
  }, [tableFilters]);

  const lancamentos = useNicashReembolsos({
    dataInicial: formattedDataInicial,
    dataFinal: formattedDataFinal,
    ...filtrosQuery,
  });

  const handleFiltrar = useCallback(() => {
    setListLoading(true);
    Promise.resolve(lancamentos.loadPage(1, LIMIT))
      .finally(() => {
        setPage(1);
        setListLoading(false);
      });
  }, [lancamentos.loadPage]);

  const handleGenerateExcel = useCallback(() => {
    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Aguarde... Estamos gerando o arquivo Excel... ',
        });

        const res = await api.get(
          `/financeiroProfissionalReembolsoExcelCreatorReport?startDate=${formattedDataInicial}&endDate=${formattedDataFinal}`,
          { responseType: 'blob' },
        );

        let fileName = 'nicash-reembolsos.xlsx';
        const contentDisposition = res?.headers?.['content-disposition'];
        if (contentDisposition) {
          fileName = contentDisposition
            .replace('attachment; filename=', '')
            .replace(/"/g, '')
            .trim();
          if (fileName.endsWith('_')) {
            fileName = fileName.slice(0, -1);
          }
        }

        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = fileName;
        alink.click();

        setLoading(false);
      } catch (err) {
        setLoading(false);
        notificationRef.current.notify({
          message: err?.response?.data?.message || 'Falha ao gerar relatório de reembolsos',
          color: 'danger',
        });
      }
    })();
  }, [formattedDataInicial, formattedDataFinal, notificationRef, setLoading]);

  useEffect(() => {
    setListLoading(true);
    Promise.resolve(lancamentos.loadPage(1, LIMIT))
      .finally(() => {
        setPage(1);
        setListLoading(false);
      });
  }, [lancamentos.loadPage]);

  const handleChangePage = useCallback((newPage) => {
    setListLoading(true);
    Promise.resolve(lancamentos.loadPage(newPage, LIMIT))
      .finally(() => {
        setPage(newPage);
        setListLoading(false);
      });
  }, [lancamentos.loadPage]);

  const handleOpenFinanceiroEdit = useCallback((financeiroId) => {
    if (!financeiroId) return;

    modalFinanceiroEdit.current?.show(
      { _id: financeiroId },
      {},
      () => {
        setListLoading(true);
        Promise.resolve(lancamentos.loadPage(page, LIMIT))
          .finally(() => setListLoading(false));
      },
    );
  }, [lancamentos.loadPage, page]);

  const handleEditAction = useCallback((row, e) => {
    if (e) e.stopPropagation();
    setSelectedRowAction(row);
    setShowEditDecisionModal(true);
  }, []);

  const handleDelete = useCallback((row, e) => {
    if (e) e.stopPropagation();
    alertRef.current.show({
      title: 'Excluir lançamento',
      message: 'Tem certeza que deseja excluir este lançamento?',
      confirmColor: 'danger',
      confirmText: 'Excluir',
      onConfirm: async () => {
        alertRef.current.close();
        const previousDatas = lancamentos.datas || [];
        lancamentos.setDatas(previousDatas.filter(
          (item) => (item.profissionalReembolsoId || item._id) !== (row.profissionalReembolsoId || row._id),
        ));
        try {
          await api.delete(`/financeiroProfissionalReembolsos/${row.profissionalReembolsoId || row._id}?profissionalId=${row.profissionalId}`);
        } catch (error) {
          notificationRef.current.notify({
            message: error?.response?.data?.message || 'Falha ao excluir lançamento',
            color: 'danger',
          });
          lancamentos.setDatas(previousDatas);
        }
      },
    });
  }, [lancamentos.datas, lancamentos.setDatas, notificationRef]);

  const handleChooseEditarReembolso = useCallback(() => {
    if (!selectedRowAction) return;
    setShowEditDecisionModal(false);
    modalNicashReembolsoEdit.current?.show(selectedRowAction, (updatedReembolso) => {
      if (updatedReembolso?._id) {
        lancamentos.setDatas((prevDatas = []) => prevDatas.map((item) => {
          const sameRow = (item.profissionalReembolsoId || item._id) === updatedReembolso._id;
          if (!sameRow) return item;

          return {
            ...item,
            profissionalReembolsoId: updatedReembolso._id,
            _id: item._id || updatedReembolso._id,
            profissionalId: updatedReembolso.profissional || item.profissionalId,
            especialista: updatedReembolso.autorizadoPor || item.especialista,
            salaAtendimento: updatedReembolso?.regra?.salaAtendimento
              ? {
                id: updatedReembolso.regra.salaAtendimento._id,
                nome: updatedReembolso.regra.salaAtendimento.nome,
              }
              : item.salaAtendimento,
            dataCompra: updatedReembolso.dataCompra || item.dataCompra,
            localCompra: updatedReembolso.localCompra || item.localCompra,
            motivoGasto: updatedReembolso.descricao || item.motivoGasto,
            valorAReceber: updatedReembolso?.regra?.tipoConta === 'conta-receber' ? updatedReembolso.valor : item.valorAReceber,
            valorAPagar: (updatedReembolso?.regra?.tipoConta === 'conta-pagar' || updatedReembolso?.regra?.tipoConta === 'conta-interna') ? updatedReembolso.valor : item.valorAPagar,
            paciente: updatedReembolso.cliente
              ? {
                id: updatedReembolso.cliente.id,
                nome: updatedReembolso.cliente.nome,
                cpf: updatedReembolso.cliente.cpf,
              }
              : item.paciente,
            numeroCartao: updatedReembolso.ultimosNumerosCartao ?? item.numeroCartao,
            tipoDocumento: updatedReembolso.tipoDocumento ?? item.tipoDocumento,
            numeroDocumento: updatedReembolso.numeroDocumento ?? item.numeroDocumento,
            formaPagamento: updatedReembolso.formaPagamento || item.formaPagamento,
            responsavelPeloGasto: updatedReembolso.responsavelTipo || item.responsavelPeloGasto,
            financeiroIds: updatedReembolso.financeiroIds || item.financeiroIds,
            updatedAt: updatedReembolso.updatedAt || item.updatedAt,
          };
        }));
      }

      setListLoading(true);
      Promise.resolve(lancamentos.loadPage(page, LIMIT))
        .finally(() => setListLoading(false));
    });
  }, [selectedRowAction, lancamentos.loadPage, lancamentos.setDatas, page]);

  const handleChooseEditarFinanceiros = useCallback(() => {
    if (!selectedRowAction) return;
    const ids = selectedRowAction.financeiroIds || [];
    setShowEditDecisionModal(false);
    if (ids.length <= 1) {
      handleOpenFinanceiroEdit(ids[0] || selectedRowAction._id);
      return;
    }
    setShowFinanceiroChoiceModal(true);
  }, [selectedRowAction, handleOpenFinanceiroEdit]);

  const handleClonar = useCallback((row, e) => {
    if (e) e.stopPropagation();

    if (row.isBlocked) {
      alertRef.current.show({
        title: 'Aviso',
        message: row.message || 'Não é possível clonar este reembolso no momento.',
        confirmText: 'Ok',
        confirmColor: 'warning',
        hideCancel: true,
        onConfirm: () => {
          alertRef.current.close();
        },
      });
      return;
    }

    const abrirFormularioClonado = () => {
      const fp = row.formaPagamento || 'nicash';
      modalNicashReembolsoCreate.current?.show(
        row,
        {
          formaPagamento: fp,
          responsavelTipo: row.responsavelPeloGasto || 'paciente',
          cliente: row?.paciente
            ? { value: row.paciente.id, cpf: row.paciente.cpf, label: row.paciente.nome }
            : '',
          descricao: '',
          valor: '',
          dataCompra: '',
          localCompra: '',
          autorizadoPor: row.especialista || '',
          tipoDocumento: '',
          numeroDocumento: '',
          ultimosNumerosCartao: fp !== 'reembolso' ? (row.numeroCartao || '') : '',
        },
        () => {
          setListLoading(true);
          Promise.resolve(lancamentos.loadPage(page, LIMIT))
            .finally(() => setListLoading(false));
        },
      );
    };

    alertRef.current.show({
      title: 'Clonagem de Reembolso',
      message:
        'Ao clonar o registro o novo reembolso irá herdar os campos de FORMA DE PAGAMENTO, RESPONSÁVEL PELO GASTO e AUTORIZADO POR do reembolso atual.\n\nDeseja continuar?',
      confirmText: 'Confirmar',
      confirmColor: 'success',
      cancelText: 'Cancelar',
      cancelColor: 'danger',
      onConfirm: () => {
        alertRef.current.close();
        abrirFormularioClonado();
      },
      onCancel: () => {
        alertRef.current.close();
      },
    });
  }, [lancamentos.loadPage, page]);

  const handleDownloadComprovante = useCallback((row, e) => {
    if (e) e.stopPropagation();
    const filename = row?.comprovante?.filename;
    const downloadName = row?.comprovante?.name || filename;
    if (!filename) return;

    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Baixando arquivo...',
        });

        const res = await api.get(`/download/${filename}/files`, { responseType: 'blob' });
        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = downloadName;
        alink.click();

        setLoading(false);
      } catch (error) {
        setLoading(false);
        notificationRef.current.notify({
          message: error?.response?.data?.message || 'Falha ao baixar comprovante',
          color: 'danger',
        });
      }
    })();
  }, [notificationRef, setLoading]);

  const actionsFormatter = useCallback((row) => (
    <ButtonGroup size="sm">
      <Button
        color="warning"
        className="p-1"
        onClick={(e) => handleEditAction(row, e)}
        title="Editar"
      >
        <i className="fa-solid fa-pen" />
      </Button>
      <Button
        color="danger"
        className="p-1"
        onClick={(e) => handleDelete(row, e)}
        title="Excluir"
      >
        <i className="fa-solid fa-trash" />
      </Button>
      <Button
        color="info"
        className="p-1"
        onClick={(e) => handleClonar(row, e)}
        title="Clonar Reembolso"
      >
        <i className="fas fa-copy" />
      </Button>
    </ButtonGroup>
  ), [handleClonar, handleDelete, handleEditAction]);

  const comprovanteFormatter = useCallback((row) => {
    if (!row?.comprovante?.filename) return '-';
    return (
      <Button
        color="info"
        size="sm"
        onClick={(e) => handleDownloadComprovante(row, e)}
      >
        <i className="fa-solid fa-download mr-1" />
        Download
      </Button>
    );
  }, [handleDownloadComprovante]);

  const dateFormatter = useCallback((row) => {
    if (!row?.dataCompra) return '-';
    try {
      return format(parseISO(row.dataCompra), 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        dataField: 'salaAtendimento',
        text: 'SALA DE ATENDIMENTO',
        formatter: (row) => row?.salaAtendimento?.nome || '-',
      },
      {
        dataField: 'dataCompra',
        text: 'DATA DA COMPRA',
        formatter: dateFormatter,
        width: '96px',
        minWidth: '96px',
      },
      {
        dataField: 'diaSemana',
        text: 'DIA DA SEMANA',
        formatter: (row) => row?.diaSemana || '-',
      },
      {
        dataField: 'localCompra',
        text: 'LOCAL DA COMPRA',
        formatter: (row) => row?.localCompra || '-',
        maxWidth: '180px',
        ellipsis: true,
      },
      {
        dataField: 'motivoGasto',
        text: 'MOTIVO DO GASTO',
        formatter: (row) => row?.motivoGasto || '-',
        maxWidth: '220px',
        ellipsis: true,
      },
      {
        dataField: 'formaPagamento',
        text: 'FORMA DE PAGAMENTO',
        formatter: (row) => row?.formaPagamento || '-',
        maxWidth: '220px',
        ellipsis: true,
      },
      {
        dataField: 'especialista',
        text: 'ESPECIALISTA',
        textFilter: true,
        filterField: 'profissionalNome',
        formatter: (row) => row?.especialista || '-',
        maxWidth: '170px',
        ellipsis: true,
      },
      {
        dataField: 'valorAReceber',
        text: 'VALOR A RECEBER',
        moneyRangeFilter: true,
        formatter: (row) => (row?.valorAReceber != null ? numberToCurrency(row.valorAReceber) : '-'),
      },
      {
        dataField: 'valorAPagar',
        text: 'VALOR A PAGAR',
        moneyRangeFilter: true,
        formatter: (row) => (row?.valorAPagar != null ? numberToCurrency(row.valorAPagar) : '-'),
      },
      {
        dataField: 'paciente',
        text: 'PACIENTE',
        textFilter: true,
        filterField: 'clienteNome',
        formatter: (row) => row?.paciente?.nome || '-',
        maxWidth: '170px',
        ellipsis: true,
      },
      {
        dataField: 'comprovante',
        text: 'COMPROVANTE',
        formatter: comprovanteFormatter,
        width: '112px',
        minWidth: '112px',
      },
      {
        dataField: 'numeroCartao',
        text: 'Nº DO CARTÃO',
        formatter: (row) => row?.numeroCartao || '-',
      },
      {
        dataField: 'tipoDocumento',
        text: 'TIPO DE DOCUMENTO',
        formatter: (row) => row?.tipoDocumento || '-',
      },
      {
        dataField: 'numeroDocumento',
        text: 'NUMERO DO DOCUMENTO',
        formatter: (row) => row?.numeroDocumento || '-',
      },
      {
        dataField: 'filial',
        text: 'FILIAL',
        formatter: (row) => row?.filial || '-',
      },
      {
        dataField: 'obs',
        text: 'OBS',
        formatter: (row) => row?.obs || '-',
        maxWidth: '180px',
        ellipsis: true,
      },
      {
        dataField: 'grupo',
        text: 'GRUPO',
        formatter: (row) => row?.grupo || '-',
      },
      {
        dataField: 'responsavelPeloGasto',
        text: 'RESPONSAVEL PELO GASTO',
        formatter: (row) => row?.responsavelPeloGasto || '-',
      },
      {
        dataField: 'cnpj',
        text: 'CNPJ',
        formatter: (row) => row?.cnpj || '-',
      },
      {
        dataField: 'razaoSocial',
        text: 'RAZAO SOCIAL',
        formatter: (row) => row?.razaoSocial || '-',
        maxWidth: '200px',
        ellipsis: true,
      },
      {
        dataField: 'acoes',
        text: 'AÇÕES',
        formatter: actionsFormatter,
        width: '120px',
        minWidth: '120px',
        alwaysVisible: true,
      },
    ],
    [dateFormatter, actionsFormatter, comprovanteFormatter],
  );

  const initialSelectedColumns = useMemo(() => {
    let saved;
    try {
      saved = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY_NICASH_REEMBOLSOS));
    } catch {
      saved = null;
    }
    const dataFields = Array.isArray(saved) && saved.length > 0 ? saved : null;
    if (dataFields) {
      return columns.filter((c) => c.alwaysVisible || dataFields.includes(c.dataField));
    }
    return columns.filter((c) => !DEFAULT_HIDDEN_COLUMNS_NICASH_REEMBOLSOS.includes(c.dataField));
  }, [columns]);

  return (
    <Container fluid>
      <AlertModal ref={alertRef} />
      <FinanceiroEdit ref={modalFinanceiroEdit} />
      <NicashReembolsoEdit ref={modalNicashReembolsoEdit} />
      <NicashReembolsoCreate ref={modalNicashReembolsoCreate} />
      <Row className="mt-3">
        <Col>
          <Card>
            <CardHeader>
              <h3>NICASH/REEMBOLSO</h3>
            </CardHeader>
            <CardBody>
              <Row className="align-items-end">
                <Col xs={12} md={3}>
                  <DatePickerMaskedInput
                    label="Data inicial"
                    placeholderText="Data inicial"
                    selected={dataInicial}
                    onChange={(date) => setDataInicial(date)}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <DatePickerMaskedInput
                    label="Data final"
                    placeholderText="Data final"
                    selected={dataFinal}
                    onChange={(date) => setDataFinal(date)}
                  />
                </Col>
                <Col xs={12} md={2} className="d-flex align-items-end mb-2 mb-md-0">
                  <Button
                    color="primary mb-3"
                    onClick={handleFiltrar}
                    disabled={loading || listLoading}
                  >
                    Filtrar
                  </Button>
                </Col>
                <Col xs={12} md={4} className="d-flex align-items-end mb-2 mb-md-0 ml-md-auto justify-content-md-end flex-wrap">
                  <Button
                    color="primary mb-3"
                    onClick={() => customTableRef.current?.openSelectColumns?.()}
                    disabled={loading || listLoading}
                  >
                    <i className="fa-solid fa-list-check mr-2" />
                    Selecionar Colunas
                  </Button>
                  <Button
                    color="success mb-3"
                    className="ml-2"
                    onClick={handleGenerateExcel}
                    disabled={loading || listLoading}
                  >
                    <i className="fa-solid fa-file-excel mr-2" />
                    Gerar Excel
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col xs={12} className="d-flex justify-content-start">
                  <Button
                    color="success"
                    onClick={() => navigate('/nicash-reembolsos/cadastrar')}
                    disabled={loading || listLoading}
                  >
                    Cadastrar Reembolso
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <CardBody>
              <CustomTable
                ref={customTableRef}
                columns={columns}
                selectableColumns
                onFiltersChange={setTableFilters}
                initialSelectedColumns={initialSelectedColumns}
                onColumnsChange={(cols) => {
                  try {
                    localStorage.setItem(
                      COLUMNS_STORAGE_KEY_NICASH_REEMBOLSOS,
                      JSON.stringify(cols.map((c) => c.dataField)),
                    );
                  } catch {
                    // ignore
                  }
                }}
                data={lancamentos.datas}
                loading={loading || listLoading}
                page={page}
                limit={LIMIT}
                totalDocs={lancamentos.totalDocs}
                totalPages={lancamentos.totalPages}
                hasPrevPage={lancamentos.hasPrevPage}
                hasNextPage={lancamentos.hasNextPage}
                paging={false}
                onChangePage={handleChangePage}
                onRowClick={() => null}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={showEditDecisionModal} toggle={() => setShowEditDecisionModal(false)} centered>
        <ModalHeader toggle={() => setShowEditDecisionModal(false)}>
          Escolha o tipo de edição
        </ModalHeader>
        <ModalBody>
          Deseja editar dados do Reembolso ou dos Financeiros?
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleChooseEditarReembolso}>
            Editar Reembolso
          </Button>
          <Button color="warning" onClick={handleChooseEditarFinanceiros}>
            Editar Financeiros
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showFinanceiroChoiceModal} toggle={() => setShowFinanceiroChoiceModal(false)} centered>
        <ModalHeader toggle={() => setShowFinanceiroChoiceModal(false)}>
          Qual financeiro deseja editar?
        </ModalHeader>
        <ModalBody>
          {(selectedRowAction?.financeiroIds || []).map((financeiroId, idx) => {
            const financeiro = selectedRowAction?.financeiros?.[idx];
            return (
              <Button
                key={financeiroId}
                color="light"
                block
                className="text-left mb-2"
                onClick={() => {
                  setShowFinanceiroChoiceModal(false);
                  handleOpenFinanceiroEdit(financeiro?.id || financeiroId);
                }}
              >
                {`${idx + 1}. ${financeiro?.tipo || 'financeiro'} - ${financeiro?.situacao || 'sem situação'}`}
              </Button>
            );
          })}
        </ModalBody>
      </Modal>
    </Container>
  );
}

export default NicashReembolsos;

